import { SettingsIcon } from "@xola/icons";
import { Label, Tooltip } from "@xola/ui-kit";
import { isEquipmentBased } from "common";
import React from "react";
import { useHistory } from "react-router-dom";
import { Experience, UnitType } from "types";
import { isExperiencePublished, isUnScheduled } from "../../../models/Experience";
import { RadioInput } from "../../Common/RadioInput";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { useHasEquipment } from "@/hooks/useHasEquipment";

interface PurchasableItemTypeSelectorProps {
    experience: Experience;
    onChange: (supportedCatalogItemTypes: UnitType[]) => void;
}

export const DEMOGRAPHIC_CATALOG_ITEM_TYPES = [UnitType.DEMOGRAPHIC, UnitType.ADDON];
export const EQUIPMENT_CATALOG_ITEM_TYPES = [UnitType.EQUIPMENT, UnitType.DEMOGRAPHIC_SECONDARY, UnitType.ADDON];

export const PurchasableItemTypeSelector = ({ experience, onChange }: PurchasableItemTypeSelectorProps) => {
    const history = useHistory();
    const isEquipment = isEquipmentBased(experience);
    const isEquipmentConfigured = useHasEquipment();
    const isUnscheduled = isUnScheduled(experience);

    const handleConfigureEquipmentClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        history.push(`/resources/equipment`);
    };
    // Lock once the experience has actual catalog items saved (demographic or equipment) — prevents switching after pricing is configured
    const hasSavedCatalogItems = (experience.catalog?.items ?? []).some(
        (item) => item.unitType === UnitType.EQUIPMENT || item.unitType === UnitType.DEMOGRAPHIC,
    );
    const isTypeLocked = Boolean(experience.id) && (isExperiencePublished(experience) || hasSavedCatalogItems);

    const lockedTooltip =
        "The purchasable unit of an experience is set only at the time of experience creation and cannot be modified.";
    const unscheduledTooltip = "Equipment-based pricing cannot be set up for experiences that do not use schedules";

    return (
        <div className="w-fit space-y-2">
            <Label>Choose the type of purchasable item to be sold as tickets</Label>

            <Tooltip content={lockedTooltip} placement="right" disabled={!isTypeLocked}>
                <RadioInput
                    checked={!isEquipment}
                    name="purchasableItemType"
                    className="text-gray-darker"
                    disabled={isTypeLocked}
                    onChange={() => !isTypeLocked && onChange(DEMOGRAPHIC_CATALOG_ITEM_TYPES)}
                >
                    Demographic
                </RadioInput>
            </Tooltip>

            <div className="flex items-center">
                <Tooltip
                    content={isUnscheduled ? unscheduledTooltip : lockedTooltip}
                    placement="right"
                    disabled={!isUnscheduled && !isTypeLocked}
                >
                    <span>
                        <RadioInput
                            checked={isEquipment}
                            name="purchasableItemType"
                            className="text-gray-darker"
                            disabled={!isEquipmentConfigured || isTypeLocked || isUnscheduled}
                            onChange={() => !isTypeLocked && !isUnscheduled && onChange(EQUIPMENT_CATALOG_ITEM_TYPES)}
                        >
                            Equipment
                        </RadioInput>
                    </span>
                </Tooltip>

                {!isEquipmentConfigured && (
                    <Tooltip
                        content="Equipment-based pricing can be set up only after equipment has been configured."
                        placement="right"
                    >
                        <span className="ml-2">
                            <SecondaryButton size="tiny" onClick={handleConfigureEquipmentClick}>
                                <SettingsIcon />
                            </SecondaryButton>
                        </span>
                    </Tooltip>
                )}
            </div>
        </div>
    );
};
